import React from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { t } from '@lingui/macro';
import { Host as MfeHost, HostConfigurationResponse, Identity, LegacyFrame } from '@economic/react-app';
import getEnvironment from './utilities/getEnvironment';
import pkg from '../package.json';
import { fetchXsrfTokenHeaders } from './utilities/fetchXsrfTokenHeaders';
import Application from './components/Application';
import External from './external';

const fetchConfiguration = async () => {
    const fetchConfig = await fetchXsrfTokenHeaders();
    return axios
        .get<HostConfigurationResponse>(`${process.env.ECONOMIC_PATH}/internal-api/mfe/host`, fetchConfig)
        .then(response => response.data);
};

const fetchLocalisation = (identity: Identity) => {
    return {
        formatting: {
            date: identity.locale.formatting.date,
        },
        locale: identity.locale.languageCode,
        texts: {
            calendar: {
                weekdaysShort: {
                    monday: t`Mon`,
                    tuesday: t`Tue`,
                    wednesday: t`Wed`,
                    thursday: t`Thu`,
                    friday: t`Fri`,
                    saturday: t`Sat`,
                    sunday: t`Sun`,
                },
                actions: {
                    previousMonth: t`Previous month`,
                    nextMonth: t`Next month`,
                    previousYear: t`Previous year`,
                    nextYear: t`Next year`,
                    today: t`Today's date`,
                },
            },
            datepicker: {
                calendar: t`Calendar`,
                expand: t`Show calendar`,
            },
            hanger: {
                close: t`Close`,
            },
            listbox: {
                loading: t`Loading...`,
                empty: t`No results`,
                allOption: t`All`,
            },
            modal: {
                drag: t`Drag`,
                close: t`Close`,
            },
            pagination: {
                label: t`Pagination`,
                showingXofYofTotal: t`Showing [X]-[Y] of [total] items`,
                pageSize: t`Items per page`,
                actions: {
                    firstPage: t`First page`,
                    firstPageWithShortcut: t`First page (Home)`,
                    nextPage: t`Next page`,
                    nextPageWithShortcut: t`Next page (Page Down)`,
                    previousPage: t`Previous page`,
                    previousPageWithShortcut: t`Previous page (Page Up)`,
                    lastPage: t`Last page`,
                    lastPageWithShortcut: t`Last page (End)`,
                    pageX: t`Go to page [X]`,
                },
            },
            searchInput: {
                inputLabel: t`Search...`,
            },
            select: {
                allOptionsSelected: t`All`,
            },
            table: {
                copy: t`Copy (c)`,
                edit: t`Edit (e)`,
                del: t`Delete (del)`,
                newSubRow: t`New sub row`,
                actions: t`Other actions`,
                loading: t`Loading...`,
            },
            toasts: {
                dismiss: t`Dismiss`,
            },
            tour: {
                back: t`Back`,
                close: t`Close`,
                last: t`Done`,
                next: t`Next`,
                open: t`Open`,
                skip: t`Skip`,
            },
        },
    };
};

const Host = () => {
    const configuration = {
        datadog: {
            applicationId: '89086963-d235-4333-b1d9-2551191bdd05',
            clientToken: 'pub2fd1f64a409d19e7277f19765c95d122',
        },
        fetcher: fetchConfiguration,
    };

    return (
        <>
            <MfeHost
                configuration={configuration}
                environment={getEnvironment()}
                localization={fetchLocalisation}
                origin={process.env.PUBLIC_URL}
                version={pkg.version}>
                <Application />
            </MfeHost>
            <LegacyFrame basePath={process.env.ECONOMIC_PATH ?? ''} />
        </>
    );
};

const Router = () => {
    if (window.location.pathname.startsWith('/external')) {
        return <External />;
    }

    return <Host />;
};

ReactDOM.render(
    <React.StrictMode>
        <Router />
    </React.StrictMode>,
    document.getElementById('root')
);
